import { create } from 'zustand';
import { api } from '../utils/api';

export interface User {
  designation?: string | null;
  tenantId?: string | null;
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  avatar?: string;
  role: { id: string; name: string; displayName: string; permissions: string[] };
  department?: { id: string; name: string; code?: string } | null;
  /** Ids of departments where this user is the head (Department.headId === user.id).
   * Added by /auth/me per TEAMS-SPEC. May be missing on older backends — defaults to []. */
  headOfDepartmentIds?: string[];
  /** Convenience flag from backend; optional. */
  isSuperAdmin?: boolean;
  /**
   * L251 — the user's reporting manager, as `GET /auth/me` returns it
   * (backend `core/auth/service.ts` getProfile). 🔒 `POST /auth/login` does NOT
   * include it, so this is absent for a session that has only just signed in —
   * see `pages/dealers/useActingDealerManager.ts`, which falls back to /auth/me
   * rather than assuming it is here.
   */
  reportsTo?: { id: string; firstName?: string | null; lastName?: string | null; email?: string | null; designation?: string | null } | null;
}

export interface TenantConfig {
  slug: string;
  name: string;
  enabledModules: string[] | null; // null = all modules (admin/platform)
  pipelineId: string | null;
  planTier: string | null; // 'FREE' | 'STARTER' | 'GROWTH' | 'ENTERPRISE' | null
  isAdmin: boolean;
  /** Custom name for the AI assistant (ENTERPRISE only). Defaults to "Kiaan". */
  aiAssistantName?: string | null;
  /**
   * LBR2-G1 tenant-gating — per-tenant UI capabilities resolved server-side
   * (`/tenants/current`). Absent/null on older responses → treated as all-OFF.
   */
  capabilities?: TenantCapabilities | null;
}

/** LBR2-G1 — resolved per-tenant UI capabilities (see backend capabilities.ts). */
export interface TenantCapabilities {
  /** Show the LBR2 discount-slab UI surfaces. */
  discountUi: boolean;
  /** Show HR-recruitment surfaces (internal dashboard + public careers). */
  recruitment: boolean;
}

interface AuthState {
  user: User | null;
  tenant: TenantConfig | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; firstName: string; lastName: string }) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  refreshTenant: () => Promise<void>;
  clearError: () => void;
}

function clearProfilePreview() {
  try { sessionStorage.removeItem('aura-view-override'); } catch { /* Storage may be unavailable. */ }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tenant: null,
  isLoading: true,
  error: null,

  login: async (email, password) => {
    try {
      set({ isLoading: true, error: null });
      const res = await api.post('/auth/login', { email, password });
      clearProfilePreview();

      // Fetch tenant config before setting isLoading=false
      let tenantData = null;
      try {
        const tenantRes = await api.get('/tenants/current');
        tenantData = tenantRes.data.data || null;
      } catch {
        // Non-critical
      }

      // Set user + tenant + isLoading in ONE state update to prevent race conditions
      set({ user: res.data.data.user, tenant: tenantData, isLoading: false });
    } catch (err: any) {
      set({ error: err.response?.data?.error || 'Login failed', isLoading: false });
      throw err;
    }
  },

  register: async (data) => {
    try {
      set({ isLoading: true, error: null });
      const res = await api.post('/auth/register', data);
      set({ user: res.data.data.user, isLoading: false });
    } catch (err: any) {
      set({ error: err.response?.data?.error || 'Registration failed', isLoading: false });
      throw err;
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearProfilePreview();
      set({ user: null, tenant: null, isLoading: false });
    }
  },

  fetchMe: async () => {
    try {
      set({ isLoading: true });
      const res = await api.get('/auth/me');

      // Fetch tenant in same call to avoid intermediate re-renders
      let tenantData = null;
      try {
        const tenantRes = await api.get('/tenants/current');
        tenantData = tenantRes.data.data || null;
      } catch {
        // Non-critical
      }

      // Set everything in ONE state update
      set({ user: res.data.data, tenant: tenantData, isLoading: false });
    } catch {
      set({ user: null, tenant: null, isLoading: false });
    }
  },

  refreshTenant: async () => {
    try {
      const tenantRes = await api.get('/tenants/current');
      set({ tenant: tenantRes.data.data || null });
    } catch {
      // Non-critical
    }
  },

  clearError: () => set({ error: null }),
}));

// Auto-fetch user on app load (only once)
let _initialized = false;
if (!_initialized) {
  _initialized = true;
  useAuthStore.getState().fetchMe();
}
